// Money helpers. The billing API speaks integer USD cents everywhere; these
// helpers are the only place cents are turned into display strings or parsed
// back from user input.

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

export interface FormatMoneyOptions {
  /** Prefix positive amounts with "+" and negatives with "−" (ledger style). */
  signed?: boolean
  /** Drop the cents when the amount is a whole dollar ($10 instead of $10.00). */
  trimWhole?: boolean
  /** Compact notation for large figures: >= $10,000 → $12.4K; $1,000–$9,999 → whole dollars. */
  compact?: boolean
}

/** Format integer cents as USD, e.g. 123456 → "$1,234.56". Non-finite → "—". */
export function formatMoney(cents: number | null | undefined, opts: FormatMoneyOptions = {}): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—'
  const rounded = Math.round(cents)
  const abs = Math.abs(rounded)
  let body: string
  if (opts.compact && abs >= 1_000_000) {
    body = usdCompact.format(abs / 100)
  } else if (opts.compact && abs >= 100_000) {
    body = usdWhole.format(Math.round(abs / 100))
  } else if (opts.trimWhole && abs % 100 === 0) {
    body = usd.format(abs / 100).replace(/\.00$/, '')
  } else {
    body = usd.format(abs / 100)
  }
  if (rounded < 0) return `${opts.signed ? '−' : '-'}${body}`
  if (opts.signed && rounded > 0) return `+${body}`
  return body
}

/**
 * Parse a user-typed dollar amount into integer cents.
 * Accepts "12", "12.5", "12.50", "$1,200.00", " 3 ". Returns null for anything
 * else (empty, negative, more than 2 decimals, letters).
 */
export function parseDollarsToCents(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) return null
    return Math.round(input * 100)
  }
  const cleaned = input.trim().replace(/^\$/, '').replace(/,/g, '').trim()
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null
  const [whole, frac = ''] = cleaned.split('.')
  return parseInt(whole, 10) * 100 + parseInt((frac + '00').slice(0, 2), 10)
}

/**
 * Parse a signed dollar amount ("-5", "+12.50", "−3") into signed cents.
 * Used by the admin manual-adjustment form. Returns null when invalid or zero.
 */
export function parseSignedDollarsToCents(input: string): number | null {
  const t = input.trim().replace(/^−/, '-')
  const negative = t.startsWith('-')
  const unsigned = t.replace(/^[+-]/, '')
  const cents = parseDollarsToCents(unsigned)
  if (cents === null || cents === 0) return null
  return negative ? -cents : cents
}

/** Cents → plain dollar string for an <input> value, e.g. 1250 → "12.50". */
export function centsToInputValue(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return ''
  return (Math.round(cents) / 100).toFixed(2)
}

/** "2026-06" → "Jun" (or "Jun 2026" with year). */
export function formatMonthLabel(month: string, withYear = false): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const d = new Date(Date.UTC(y, m - 1, 1))
  return d.toLocaleDateString('en-US', { month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' })
}
